import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {BRAND_DEPARTMENT_PAGE_SIZE} from '../constants/GlobalConstants';
import {AirtableBrand} from '../models/airtable';
import apiService from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';
import {logger, serializeError} from '../utils/logger';

import {GIFT_CARDS_TILE, SPECIALS_TILE} from './rangeStore';
import type {RootStore} from './rootStore';

export class BrandsStore {
  rootStore: RootStore;
  brands: AirtableBrand[] = [];
  loadingLinked = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);

    this.reset();
  }

  loadLinkedProductsForBrand = async (brand: {brand: string}) => {
    const linked: Record<string, OrderLine[]> = {};

    await Promise.all(
      this.rootStore.rangeStore?.departments
        ?.filter(
          dep =>
            dep.parent &&
            dep.parent.id === this.rootStore.rangeStore.parentDepartment?.id &&
            dep.name !== 'Balanced Lifestyle' &&
            dep.id !== SPECIALS_TILE &&
            dep.id !== GIFT_CARDS_TILE,
        )
        .map(async department => {
          logger.debug('BrandsStore', 'Loading products for brand and department', {
            brand: brand.brand,
            department: department.name,
          });
          const products = (await apiService.getProductsByDepartmentAndBrand(
            brand,
            department,
          )) as unknown as OrderLine[];
          // DIS-1951: a full page may mean MFL holds more lines than the page shows.
          if (products.length >= BRAND_DEPARTMENT_PAGE_SIZE) {
            logger.warn('BrandsStore', 'Brand department reached the page size; lines may be cut', {
              brand: brand.brand,
              department: department.name,
              length: products.length,
            });
          }
          if (products.length) {
            linked[department.name] = products;
          }
        }),
    );
    return linked;
  };

  reset = () => {
    runInAction(() => {
      this.loadingLinked = false;
    });
  };

  loadBrands = async () => {
    try {
      const brands = await apiService.getBrands();
      runInAction(() => {
        this.brands = brands;
      });
    } catch (err) {
      logger.error('BrandsStore', 'Error loading brands', serializeError(err));
    }
  };
}
